import {
  defaultThemeStyle,
  detachedValues,
  editsThemeStyle,
  themeStyleById,
  themeStylesOf,
} from '../../shared/theme/themeStyles';
import { proposalTableDesign } from '../../shared/tables/proposalTableStyle';
import { classicTableStyleFromDesign } from '../../shared/theme/themeConfiguration';
import { boxPropsFromStyle } from '../../shared/boxes/styleTemplate';
import {
  classicValuesFromSite, seedKeys, siteValuesFromClassic, styleKeys, withoutSeeds,
} from '../../shared/theme/styleSchema';
import { TABLE_STYLE_SCHEMA } from '../../shared/tables/tableStyleSchema';
import { TABLE_THEME_STYLE_PROPS } from '../blocks/web/EditableDataTable/tableStyles';
import { INFO_CARD_THEME_STYLE_PROPS } from '../../shared/boxes/infoCard/infoCardStyles';
import { INFO_CARD_STYLE_SCHEMA } from '../../shared/boxes/infoCard/infoCardProps';
import { ACCORDION_PANEL_STYLE_SCHEMA } from '../../shared/boxes/accordionPanel/accordionPanelProps';
import { BUTTON_STYLE_SCHEMA } from '../../shared/boxes/button/buttonStyles';
import { CAROUSEL_SEED_PROPS, CAROUSEL_STYLE_PROPS } from '../blocks/web/CarouselBlock/carouselStyles';
import { TABS_STYLE_SCHEMA } from '../blocks/web/TabsBlock/tabsStyleSchema';
import { TIMELINE_STYLE_SCHEMA } from '../blocks/web/TimelineBlock/timelineStyleSchema';
import { SCROLL_STORY_STYLE_SCHEMA } from '../blocks/web/ScrollStoryBlock/scrollStoryStyleSchema';
import { HOTSPOT_IMAGE_STYLE_SCHEMA } from '../blocks/web/HotspotImageBlock/hotspotImageStyleSchema';
import { IMAGE_COMPARISON_STYLE_SCHEMA } from '../blocks/web/ImageComparisonBlock/imageComparisonStyleSchema';
import { VIDEO_STYLE_SCHEMA } from '../blocks/web/VideoBlock/videoStyleSchema';
import { dropEmptySections } from './fields/puckSectionField';

// Un style du thème pose ce qu'un style de la galerie pose, et ce qu'il est
// seul à poser (cf. shared/theme/styleSchema).
const themedProps = (schema) => styleKeys(schema, ['gallery', 'theme']);

// Composants propres au site : leur style se range tel quel, sans conversion
// par le schéma ; seules leurs valeurs propres en sont lues.
const siteSchemaSeeds = (schema) => seedKeys(schema, themedProps(schema));

const pick = (values, keys) => Object.fromEntries(
  keys.filter((key) => values?.[key] !== undefined).map((key) => [key, values[key]]),
);

const omit = (values, keys) => Object.fromEntries(
  Object.entries(values || {}).filter(([key]) => !keys.includes(key)),
);

/**
 * Bloc dont un style du thème pose les props à plat, au format du bloc : la
 * Carte info et le Volet (styles au format du classique, convertis par leur
 * schéma), et les composants propres au site.
 *
 * `gallery` : le champ qui porte la galerie de styles du bloc, sans objet dans
 * le panneau Thème (on y règle un style, on n'en choisit pas un).
 * `contentFields` : champs composites qui règlent du style à côté d'une prop
 * de contenu (l'icône d'une Carte info et sa forme, sa taille, sa couleur).
 * `seedProps` : les valeurs propres au bloc (cf. `seed`, shared/theme/
 * styleSchema), lues dans le schéma ; le Carrousel, sans schéma, les donne.
 * `compositeFields` : champs déclarés sur une prop suivie mais qui portent
 * aussi des valeurs propres ; ils restent affichés sur un bloc lié et y
 * masquent eux-mêmes leurs réglages suivis (`useThemeLockedProp`).
 */
const flatThemedBlock = (type, styleProps, schema = null, {
  gallery = null, contentFields = [], compositeFields = [], fallbackValues = {},
  seedProps = schema ? seedKeys(schema, styleProps) : [],
} = {}) => ({
  type,
  styleProps,
  gallery,
  compositeFields,
  seedProps,
  lockedProps: styleProps.filter((key) => !seedProps.includes(key)),
  fieldKeys: [...styleProps, ...contentFields].filter((key) => key !== gallery),
  read: (props) => props || {},
  write: (props, values) => ({ ...props, ...values }),
  edited: (changed) => styleProps.some((key) => changed[key] && !seedProps.includes(key)),
  valuesOf: (style) => {
    const values = { ...fallbackValues, ...boxPropsFromStyle(style) };
    return pick(schema ? siteValuesFromClassic(schema, values) : values, styleProps);
  },
  toStyle: (style, props) => {
    const values = pick(props, styleProps);
    return { ...style, ...(schema ? classicValuesFromSite(schema, values) : values) };
  },
});

/**
 * Par type de composant du thème : son bloc Puck, les props qu'un style pose
 * (`styleProps`), parmi les valeurs que lit `read`, celles que le bloc garde
 * pour lui (`seedProps`) et celles qu'il suit (`lockedProps`), les champs de l'inspecteur qui les règlent (`fieldKeys`),
 * où le bloc les range (le Tableau dans `design`, les autres à plat), et
 * comment un style du thème devient ces valeurs (`valuesOf`) et l'inverse
 * (`toStyle`, réglages du panneau Thème) — pour le Tableau, les conversions
 * des blocs générés depuis un document.
 */
export const THEMED_BLOCKS = {
  table: {
    type: 'EditableDataTable',
    styleProps: ['design'],
    seedProps: seedKeys(TABLE_STYLE_SCHEMA, TABLE_THEME_STYLE_PROPS),
    lockedProps: withoutSeeds(TABLE_STYLE_SCHEMA, TABLE_THEME_STYLE_PROPS),
    // `design` porte la galerie ; les réglages vivent dans `_designSettings`.
    fieldKeys: ['_designSettings'],
    read: (props) => props?.design || {},
    write: (props, values) => ({ ...props, design: values }),
    edited: (changed) => !!changed.design,
    valuesOf: (style, configuration) => pick(
      proposalTableDesign(style.id, configuration),
      TABLE_THEME_STYLE_PROPS,
    ),
    toStyle: (style, props) => classicTableStyleFromDesign(props.design, style, style),
  },
  infoCard: flatThemedBlock('InfoCard', INFO_CARD_THEME_STYLE_PROPS, INFO_CARD_STYLE_SCHEMA, {
    gallery: 'titleStyle', contentFields: ['icon'],
  }),
  accordion: flatThemedBlock(
    'AccordionPanel',
    themedProps(ACCORDION_PANEL_STYLE_SCHEMA),
    ACCORDION_PANEL_STYLE_SCHEMA,
  ),
  button: flatThemedBlock('ButtonBlock', themedProps(BUTTON_STYLE_SCHEMA), null, {
    gallery: 'radius', contentFields: ['icon'], seedProps: siteSchemaSeeds(BUTTON_STYLE_SCHEMA),
  }),
  // Les styles de la galerie ne touchent pas aux textes : `textStyles` reste
  // hors de CAROUSEL_STYLE_PROPS, qui sert aussi à reconnaître un style.
  carousel: flatThemedBlock('CarouselBlock', [...CAROUSEL_STYLE_PROPS, 'textStyles'], null, {
    gallery: 'loop', seedProps: CAROUSEL_SEED_PROPS, compositeFields: ['maxSlidesVisible', 'autoplay'], fallbackValues: { thumbnails: false },
  }),
  scrollStory: flatThemedBlock('ScrollStoryBlock', themedProps(SCROLL_STORY_STYLE_SCHEMA), null),
  hotspotImage: flatThemedBlock('HotspotImageBlock', themedProps(HOTSPOT_IMAGE_STYLE_SCHEMA), null),
  imageComparison: flatThemedBlock('ImageComparisonBlock', themedProps(IMAGE_COMPARISON_STYLE_SCHEMA), null),
  video: flatThemedBlock('VideoBlock', themedProps(VIDEO_STYLE_SCHEMA), null),
  tabs: flatThemedBlock('TabsBlock', themedProps(TABS_STYLE_SCHEMA), null, {
    seedProps: siteSchemaSeeds(TABS_STYLE_SCHEMA),
  }),
  timeline: flatThemedBlock('TimelineBlock', themedProps(TIMELINE_STYLE_SCHEMA), null, {
    seedProps: siteSchemaSeeds(TIMELINE_STYLE_SCHEMA),
  }),
};

/** Le type de composant du thème d'un type de bloc Puck. */
export const THEMED_BLOCK_KINDS = Object.fromEntries(
  Object.entries(THEMED_BLOCKS).map(([kind, { type }]) => [type, kind]),
);

const configurationOf = (metadata) => metadata?.themeConfiguration || null;

/** Les valeurs qu'un style du thème pose sur un bloc de ce type. */
export function themeStyleValues(kind, style, configuration) {
  return THEMED_BLOCKS[kind].valuesOf(style, configuration);
}

/** Celles que le bloc suit tant qu'il est lié : sans ses valeurs propres. */
function lockedStyleValues(kind, style, configuration) {
  return omit(themeStyleValues(kind, style, configuration), THEMED_BLOCKS[kind].seedProps);
}

/** Les props qu'un bloc affiche : celles du style du thème qu'il suit. */
export function themedBlockProps(kind, props, metadata) {
  const configuration = configurationOf(metadata);
  const style = themeStyleById(themeStylesOf(configuration, kind), props.themeStyleId);
  if (!style) return props;
  const block = THEMED_BLOCKS[kind];
  return block.write(props, { ...block.read(props), ...lockedStyleValues(kind, style, configuration) });
}

/**
 * Le bloc quitte son style, à la demande (clic sur la tuile « Personnalisé ») : il
 * garde l'aspect qu'il avait, retouches du thème comprises — sa copie peut
 * dater d'avant.
 */
export function detachThemeStyle(kind, props, configuration) {
  const block = THEMED_BLOCKS[kind];
  const style = themeStyleById(themeStylesOf(configuration, kind), props.themeStyleId);
  const values = style
    ? { ...block.read(props), ...lockedStyleValues(kind, style, configuration) }
    : block.read(props);
  return { ...block.write(props, values), themeStyleId: null };
}

/**
 * `resolveFields` des blocs thémables : lié à un style du thème, le bloc ne
 * montre pas les réglages qu'il suit — les retoucher le détacherait sans
 * qu'on l'ait voulu. Restent le contenu et ses valeurs propres ; pour les
 * régler, on le détache (cf. ThemeStyleGallery).
 */
export function themedBlockResolveFields(kind) {
  const { lockedProps, gallery, compositeFields = [] } = THEMED_BLOCKS[kind];
  const hidden = lockedProps.filter((key) => key !== gallery && !compositeFields.includes(key));
  return (data, { fields }) => {
    if (data.props?.themeStyleId == null) return fields;
    return dropEmptySections(omit(fields, hidden));
  };
}

/** Le bloc suit ce style du thème : son id, et ses valeurs recopiées. */
export function linkThemeStyle(kind, props, style, configuration) {
  const block = THEMED_BLOCKS[kind];
  const values = { ...block.read(props), ...themeStyleValues(kind, style, configuration) };
  // L'id se pose APRÈS les valeurs : la Carte info les range à plat, avec
  // l'ancien `themeStyleId` parmi elles.
  return { ...block.write(props, values), themeStyleId: style.id };
}

/**
 * `resolveData` des blocs thémables :
 * - déposé : il prend le style Défaut du thème, s'il y en a un ;
 * - réglé à la main (une valeur suivie éditée, qui ne correspond plus au
 *   style) : il se détache et passe en « Personnalisé », avec l'aspect qu'il
 *   avait. Filet : l'inspecteur masque ces réglages (`themedBlockResolveFields`),
 *   mais un champ composite ou un dispatch peut encore les écrire. Une valeur
 *   propre au bloc (`seed`) ne détache jamais.
 *
 * Seul `insert` pose le défaut : au chargement, un bloc sans style est un
 * bloc enregistré tel quel, on n'y touche pas.
 */
export function themedBlockResolveData(kind) {
  const block = THEMED_BLOCKS[kind];
  return (data, {
    trigger, changed = {}, lastData, metadata,
  }) => {
    const { props } = data;
    const configuration = configurationOf(metadata);
    const styles = themeStylesOf(configuration, kind);

    if (trigger === 'insert') {
      const fallback = defaultThemeStyle(configuration, kind);
      if (props.themeStyleId !== undefined || !fallback) return data;
      return { ...data, props: linkThemeStyle(kind, props, fallback, configuration) };
    }

    if (trigger !== 'replace' || props.themeStyleId == null || !block.edited(changed)) return data;

    // Pas `changed.themeStyleId` : le cache de Puck (`lastData`) est périmé
    // pour un bloc que l'éditeur a réécrit par `setData` (box emballée dans un
    // Bloc au dépôt). On regarde ce que l'édition a fait aux valeurs du style.
    const style = themeStyleById(styles, props.themeStyleId);
    const styleValues = style ? lockedStyleValues(kind, style, configuration) : null;
    const lastValues = block.read(lastData?.props);
    if (styleValues && !editsThemeStyle(block.read(props), lastValues, styleValues)) return data;

    const values = detachedValues(block.read(props), lastValues, styleValues);
    return { ...data, props: { ...block.write(props, values), themeStyleId: null } };
  };
}

/**
 * Réécrit les blocs thémables d'une structure (web_data, données Puck, zones),
 * en ne recopiant que les branches touchées.
 *
 * @param {(kind: string, block: object) => object} mapBlock
 */
export function mapThemedBlocksDeep(value, mapBlock) {
  if (Array.isArray(value)) {
    let changed = false;
    const next = value.map((item) => {
      const mapped = mapThemedBlocksDeep(item, mapBlock);
      if (mapped !== item) changed = true;
      return mapped;
    });
    return changed ? next : value;
  }
  if (!value || typeof value !== 'object') return value;

  const kind = THEMED_BLOCK_KINDS[value.type];
  if (kind && value.props) return mapBlock(kind, value);

  let changed = false;
  const next = {};
  Object.entries(value).forEach(([key, item]) => {
    const mapped = mapThemedBlocksDeep(item, mapBlock);
    if (mapped !== item) changed = true;
    next[key] = mapped;
  });
  return changed ? next : value;
}

/**
 * Le bloc tout juste déposé prend le style par défaut du thème. Pendant de
 * `resolveData` (trigger `insert`) pour les dépôts que l'éditeur réécrit
 * aussitôt (box lâchée à la racine, emballée dans un Bloc) : ce `setData`
 * part de l'état d'avant `resolveData` et en effacerait le résultat.
 */
export function withDefaultThemeStyle(data, blockId, metadata) {
  const configuration = configurationOf(metadata);
  return mapThemedBlocksDeep(data, (kind, block) => {
    const fallback = defaultThemeStyle(configuration, kind);
    if (block.props.id !== blockId || block.props.themeStyleId !== undefined || !fallback) {
      return block;
    }
    return { ...block, props: linkThemeStyle(kind, block.props, fallback, configuration) };
  });
}
